import { useEffect, useState, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Plus, Gift, Users, Settings, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { differenceInDays, format, parseISO, setYear } from "date-fns";
import SEOHead from "@/components/SEOHead";

import type { Tables } from "@/integrations/supabase/types";
import { useNavigate } from "react-router-dom";
import AddEditPerson from "@/components/AddEditPerson";
import ContactDetail from "@/components/ContactDetail";
import ReminderBanner from "@/components/ReminderBanner";
import InstallBanner from "@/components/InstallBanner";
import DailyReflectionCard from "@/components/DailyReflectionCard";
import GlobalOccasionCard from "@/components/GlobalOccasionCard";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { OCCASION_TYPES, type OccasionType } from "@/lib/occasion-types";
import NewBabyPrompt from "@/components/NewBabyPrompt";
import { findSimilarContacts } from "@/lib/duplicate-detection";
import { useTierGate } from "@/hooks/useTierGate";
import { PaywallModal } from "@/components/PaywallModal";
import GiftBrowser from "@/components/GiftBrowser";

type Contact = Tables<"contacts">;

interface OccasionRecord {
  id: string;
  contact_id: string;
  occasion_type: OccasionType;
  label: string | null;
  date: string;
  is_recurring: boolean;
  converted_to_contact: boolean;
}

function getDaysUntilBirthday(birthdayStr: string): number {
  const today = new Date();
  const birthday = parseISO(birthdayStr);
  let next = setYear(birthday, today.getFullYear());
  today.setHours(0, 0, 0, 0);
  next.setHours(0, 0, 0, 0);
  if (next < today) {
    next = setYear(birthday, today.getFullYear() + 1);
    next.setHours(0, 0, 0, 0);
  }
  return differenceInDays(next, today);
}

function getDaysUntilOccasion(dateStr: string, isRecurring: boolean): number | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = parseISO(dateStr);

  if (!isRecurring) {
    const target = new Date(d);
    target.setHours(0, 0, 0, 0);
    const diff = differenceInDays(target, today);
    return diff < 0 ? null : diff;
  }

  let next = setYear(d, today.getFullYear());
  next.setHours(0, 0, 0, 0);
  if (next < today) {
    next = setYear(d, today.getFullYear() + 1);
    next.setHours(0, 0, 0, 0);
  }
  return differenceInDays(next, today);
}

const RELATIONSHIP_EMOJI: Record<string, string> = {
  Partner: "💕",
  Parent: "🏠",
  Child: "👶",
  Sibling: "👫",
  Friend: "🤝",
  Colleague: "💼",
  Other: "✨",
};

function getGreeting(name: string): string {
  const hour = new Date().getHours();
  if (hour < 12) return `Good morning, ${name}.`;
  if (hour < 17) return `Good afternoon, ${name}.`;
  return `Good evening, ${name}.`;
}

interface ContactWithNextOccasion extends Contact {
  nextOccasionDays: number;
  nextOccasionType: OccasionType;
  nextOccasionLabel: string | null;
  nextOccasionDate: string;
}

const Dashboard = () => {
  const { user, signOut } = useAuth();
  const { toast: uiToast } = useToast();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [occasions, setOccasions] = useState<OccasionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [reminderLeadTime, setReminderLeadTime] = useState("3_days");
  const [hasUserBirthday, setHasUserBirthday] = useState(true);
  const [showBirthdayNudge, setShowBirthdayNudge] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [initialMessageContext, setInitialMessageContext] = useState<{ type: OccasionType; label: string } | null>(null);
  const [babyPromptData, setBabyPromptData] = useState<{ babyName: string; date: string; contactName: string; atContactLimit: boolean; occasionDate: string } | null>(null);
  const [convertedBabyDate, setConvertedBabyDate] = useState<string | null>(null);
  const [authProvider, setAuthProvider] = useState<string | null>(null);
  const [backupEmail, setBackupEmail] = useState<string | null>(null);
  const [showAppleBanner, setShowAppleBanner] = useState(false);
  
  // Tier gate
  const { isAtContactLimit, isPlus } = useTierGate();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallProps, setPaywallProps] = useState({ title: "", benefit: "" });
  const [giftBrowserOpen, setGiftBrowserOpen] = useState(false);

  const pwa = usePWAInstall();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    if (payment === "success") {
      uiToast({
        title: "Payment confirmed 🎁",
        description: "Your gift order is confirmed. View it in Gift orders.",
        duration: 6000,
      });
      window.history.replaceState({}, "", "/dashboard");
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!user) return;
    const [contactsRes, occasionsRes] = await Promise.all([
      supabase.from("contacts").select("*").eq("user_id", user.id),
      supabase.from("occasions").select("*").eq("user_id", user.id),
    ]);
    if (contactsRes.data) setContacts(contactsRes.data);
    if (occasionsRes.data) setOccasions(occasionsRes.data as OccasionRecord[]);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const init = async () => {
      const [contactsRes, profileRes, occasionsRes] = await Promise.all([
        supabase.from("contacts").select("*").eq("user_id", user.id),
        supabase.from("profiles").select("first_name, reminder_lead_time, user_birthday").eq("user_id", user.id).maybeSingle(),
        supabase.from("occasions").select("*").eq("user_id", user.id),
      ]);
      if (contactsRes.data) setContacts(contactsRes.data);
      if (occasionsRes.data) setOccasions(occasionsRes.data as OccasionRecord[]);
      if (profileRes.data?.first_name) setFirstName(profileRes.data.first_name);
      if (profileRes.data?.reminder_lead_time) setReminderLeadTime(profileRes.data.reminder_lead_time);
      const hasBday = !!profileRes.data?.user_birthday;
      setHasUserBirthday(hasBday);
      const dismissed = localStorage.getItem("circle_birthday_nudge_dismissed") === "true";
      setShowBirthdayNudge(!hasBday && !dismissed);
      const provider = (profileRes.data as any)?.auth_provider ?? null;
      setAuthProvider(provider);
      setBackupEmail((profileRes.data as any)?.backup_email ?? null);
      const appleDismissed = localStorage.getItem("circle_apple_backup_email_dismissed") === "true";
      setShowAppleBanner(provider === "apple" && !appleDismissed && !(profileRes.data as any)?.backup_email);
      setLoading(false);
    };
    init();
  }, [user]);

  // Check for upgrade intent from landing page CTA
  useEffect(() => {
    if (loading) return;
    const intent = localStorage.getItem('upgrade_intent');
    if (intent === 'plus') {
      localStorage.removeItem('upgrade_intent');
      if (!isPlus) {
        setPaywallProps({
          title: "Unlock Dearestly Plus",
          benefit: "Unlimited contacts, all occasion types, unlimited messages, and more.",
        });
        setPaywallOpen(true);
      }
    }
  }, [loading, isPlus]);

  const openAdd = () => {
    const unlockedCount = contacts.filter(c => !(c as any).locked).length;
    if (isAtContactLimit(unlockedCount)) {
      setPaywallProps({
        title: "Unlimited contacts is a Dearestly Plus feature",
        benefit: "Add everyone who matters — no limits on the people in your circle.",
      });
      setPaywallOpen(true);
      return;
    }
    setEditingContact(null);
    setSheetOpen(true);
  };

  const openEdit = (contact: Contact) => {
    setEditingContact(contact);
    setSheetOpen(true);
  };

  const openDetail = (contact: Contact) => {
    setSelectedContact(contact);
    setDetailOpen(true);
  };

  // Build sorted list with next occasion info
  const contactsWithNext: ContactWithNextOccasion[] = contacts.map((contact) => {
    const birthdayDays = getDaysUntilBirthday(contact.birthday);
    let nextDays = birthdayDays;
    let nextType: OccasionType = "birthday";
    let nextLabel: string | null = null;
    let nextDate: string = contact.birthday;

    const contactOccasions = occasions.filter((o) => o.contact_id === contact.id);
    for (const occ of contactOccasions) {
      if (occ.occasion_type === "birthday") continue;
      const days = getDaysUntilOccasion(occ.date, occ.is_recurring);
      if (days !== null && days < nextDays) {
        nextDays = days;
        nextType = occ.occasion_type;
        nextLabel = occ.label;
        nextDate = occ.date;
      }
    }

    return { ...contact, nextOccasionDays: nextDays, nextOccasionType: nextType, nextOccasionLabel: nextLabel, nextOccasionDate: nextDate };
  });

  const sorted = [...contactsWithNext].sort((a, b) => a.nextOccasionDays - b.nextOccasionDays);

  if (loading) {
    return (
      <>
        <SEOHead title="Dashboard — Dearestly." description="Your Dearestly. dashboard — upcoming occasions, reminders, and the people who matter." path="/dashboard" noindex />
        <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm">
          <div className="mx-auto flex max-w-2xl items-center justify-between px-6 py-4">
            <div>
            <h1 className="font-serif text-2xl font-semibold text-foreground">Dearestly.</h1>
              <div className="mt-0.5 h-0.5 w-12 rounded-full bg-accent" />
            </div>
          </div>
        </header>
        <main className="mx-auto max-w-2xl px-6 py-8">
          <div className="mb-6 h-4 w-40 rounded bg-muted animate-pulse" />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-2">
                    <div className="h-5 w-32 rounded bg-muted animate-pulse" />
                    <div className="h-4 w-24 rounded bg-muted animate-pulse" />
                  </div>
                  <div className="ml-4 flex flex-col items-center rounded-xl bg-accent/10 px-3 py-2">
                    <div className="h-4 w-4 rounded bg-muted animate-pulse mb-1" />
                    <div className="h-6 w-6 rounded bg-muted animate-pulse" />
                    <div className="h-2 w-8 rounded bg-muted animate-pulse mt-1" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </>
    );
  }

  return (
    <>
      <SEOHead title="Dashboard — Dearestly." description="Your Dearestly. dashboard — upcoming occasions, reminders, and the people who matter." path="/dashboard" noindex />
      <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="font-serif text-2xl font-semibold text-foreground">Dearestly.</h1>
            <div className="mt-0.5 h-0.5 w-12 rounded-full bg-accent" />
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground"
              aria-label="Send a gift"
              onClick={() => setGiftBrowserOpen(true)}
            >
              <Gift className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} className="text-muted-foreground hover:text-foreground" aria-label="Settings">
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-8 animate-fade-in">
        {firstName && (
          <p className="mb-6 text-sm text-muted-foreground">{getGreeting(firstName)}</p>
        )}

        <DailyReflectionCard />

        <GlobalOccasionCard
          onWriteMessage={(contactId, occasionLabel) => {
            const c = contacts.find((x) => x.id === contactId);
            if (!c) return;
            setSelectedContact(c);
            setInitialMessageContext({ type: "special_date", label: occasionLabel });
            setDetailOpen(true);
          }}
        />

        {showAppleBanner && (
          <div className="mb-4 rounded-2xl border-l-[3px] border-l-accent bg-card p-4 shadow-sm">
            <p className="text-sm font-medium text-foreground mb-1">💛 You signed in with Apple.</p>
            <p className="text-xs text-muted-foreground mb-3">
              Consider adding a backup email in Settings → Profile in case you ever lose access to your Apple ID.
            </p>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/settings/profile")}
                className="text-sm font-medium text-accent hover:underline"
              >
                Add backup email
              </button>
              <button
                onClick={() => {
                  localStorage.setItem("circle_apple_backup_email_dismissed", "true");
                  setShowAppleBanner(false);
                }}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Maybe later
              </button>
            </div>
          </div>
        )}

        {showBirthdayNudge && (
          <div className="mb-4 rounded-2xl border-l-[3px] border-l-accent bg-card p-4 shadow-sm">
            <p className="text-sm text-foreground mb-3">
              Want us to celebrate you on your birthday? Add your birthday to your profile 🎂
            </p>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/settings/profile")}
                className="text-sm font-medium text-accent hover:underline"
              >
                Add birthday
              </button>
              <button
                onClick={() => {
                  localStorage.setItem("circle_birthday_nudge_dismissed", "true");
                  setShowBirthdayNudge(false);
                }}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Maybe later
              </button>
            </div>
          </div>
        )}

        <ReminderBanner
          contacts={contacts}
          reminderLeadTime={reminderLeadTime}
          getDaysUntilBirthday={getDaysUntilBirthday}
          onContactClick={openDetail}
        />

        <InstallBanner
          isInstalled={pwa.isInstalled}
          isIOS={pwa.isIOS}
          isAndroid={pwa.isAndroid}
          isDesktop={pwa.isDesktop}
          canPrompt={pwa.canPrompt}
          bannerDismissed={pwa.bannerDismissed}
          promptInstall={pwa.promptInstall}
          dismissBanner={pwa.dismissBanner}
          dismissIOSGuide={pwa.dismissIOSGuide}
        />

        {/* Empty state */}
        {sorted.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-accent/10">
              <Users className="h-8 w-8 text-accent" />
            </div>
            <h2 className="mb-3 font-serif text-2xl font-semibold text-foreground">
              Your Dearestly is empty.
            </h2>
            <p className="mb-8 max-w-sm text-body text-muted-foreground">
              Add the first person who matters. Then tell us who they are — the more we know, the more personal every message becomes.
            </p>
            <Button size="lg" onClick={openAdd}>
              <Plus className="mr-2 h-4 w-4" />
              Add your first person →
            </Button>
          </div>
        )}

        {/* Contact cards — staggered fade-up */}
        {sorted.length > 0 && (
          <motion.div
            initial="hidden"
            animate="show"
            variants={reduce ? undefined : { hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}
            className="space-y-3"
          >
            {sorted.map((contact) => {
              const daysUntil = contact.nextOccasionDays;
              const isUpcoming = daysUntil <= 30;
              const nextConfig = OCCASION_TYPES[contact.nextOccasionType];
              const nextLabel = contact.nextOccasionType === "special_date" && contact.nextOccasionLabel
                ? contact.nextOccasionLabel
                : nextConfig.label;
              const birthdayDate = parseISO(contact.birthday);

              const isLocked = !!(contact as any).locked;

              return (
                <motion.button
                  key={contact.id}
                  variants={reduce ? undefined : {
                    hidden: { opacity: 0, y: 12 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
                  }}
                  onClick={() => openDetail(contact)}
                  className={`relative w-full rounded-2xl border bg-card p-5 text-left shadow-sm transition-shadow hover:shadow-md ${
                    isUpcoming ? "border-l-4 border-l-accent" : "border-border"
                  } ${isLocked ? "opacity-60" : ""}`}
                >
                  {isLocked && (
                    <Lock className="absolute top-3 right-3 text-accent" size={14} />
                  )}
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate font-serif text-lg font-semibold text-foreground">{contact.full_name}</h3>
                        <span className="text-sm">{RELATIONSHIP_EMOJI[contact.relationship] ?? "✨"}</span>
                      </div>
                      <p className={`mt-0.5 text-sm ${isUpcoming ? "text-accent font-medium" : "text-muted-foreground"}`}>
                        {nextLabel} · {format(parseISO(contact.nextOccasionDate), "MMM d")}
                      </p>
                    </div>
                    <div className="ml-4 flex flex-col items-center rounded-xl bg-accent/10 px-3 py-2">
                      <span className="mb-0.5 text-sm">{nextConfig.emoji}</span>
                      <span className="text-lg font-bold leading-tight text-foreground">{daysUntil}</span>
                      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        {daysUntil === 1 ? "day" : "days"}
                      </span>
                    </div>
                  </div>
                  {contact.notes && (
                    <p className="mt-2 truncate text-xs text-muted-foreground/70">📝 {contact.notes}</p>
                  )}
                </motion.button>
              );
            })}
          </motion.div>
        )}

        {/* D8 — Dashboard upgrade nudge for free users */}
        {!isPlus && sorted.length > 0 && (
          <p className="text-center text-sm text-muted-foreground py-4">
            Upgrade to{' '}
            <button
              onClick={() => {
                setPaywallProps({
                  title: "Unlock Dearestly Plus",
                  benefit: "Unlimited contacts, all occasion types, unlimited messages, and more.",
                });
                setPaywallOpen(true);
              }}
              className="font-medium underline-offset-2 hover:underline"
              style={{ color: '#C9A84C' }}
            >
              Dearestly. Plus
            </button>
            {' '}to unlock unlimited contacts, all occasions, and more.
          </p>
        )}
      </main>

      {/* FAB */}
      {sorted.length > 0 && (
        <div className="fixed bottom-8 right-8">
          <Button size="lg" className="h-14 w-14 rounded-full shadow-lg" aria-label="Add person" onClick={openAdd}>
            <Plus className="h-5 w-5" />
          </Button>
        </div>
      )}

      {/* Add/Edit Sheet */}
      <AddEditPerson
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        contact={editingContact}
        onSaved={fetchData}
        existingContacts={contacts}
        onViewContact={(c) => {
          setSelectedContact(c);
          setDetailOpen(true);
        }}
        onBabyPrompt={(data) => {
          setBabyPromptData(data);
        }}
        convertedBabyDate={convertedBabyDate}
        onConvertedBabyDateHandled={() => setConvertedBabyDate(null)}
      />

      {/* New Baby Prompt - rendered at top level to avoid z-index issues */}
      {babyPromptData && (() => {
        // Check for duplicate baby name
        const babyDuplicateMatches = babyPromptData.babyName
          ? findSimilarContacts(babyPromptData.babyName, contacts)
          : [];
        const dupWarning = babyDuplicateMatches.length > 0
          ? { matchedName: babyDuplicateMatches[0].contact.full_name, matchedContactId: babyDuplicateMatches[0].contact.id }
          : null;

        return (
          <NewBabyPrompt
            open={!!babyPromptData}
            onOpenChange={(v) => { if (!v) setBabyPromptData(null); }}
            babyName={babyPromptData.babyName}
            occasionDate={babyPromptData.date}
            contactName={babyPromptData.contactName}
            atContactLimit={babyPromptData.atContactLimit}
            duplicateWarning={dupWarning}
            onViewExisting={(contactId) => {
              const matched = contacts.find(c => c.id === contactId);
              if (matched) {
                setBabyPromptData(null);
                openDetail(matched);
              }
            }}
            onAddToCircle={async (prefillData) => {
              const occasionDate = babyPromptData?.occasionDate;
              setBabyPromptData(null);
              if (!user) return;
              const { error } = await supabase.from("contacts").insert({
                full_name: prefillData.name,
                birthday: prefillData.birthday,
                relationship: prefillData.relationship,
                user_id: user.id,
              });
              if (error) {
                if (error.message?.includes("Contact limit reached")) {
                  uiToast({ title: "You've reached the contact limit. Upgrade to Dearestly Plus for unlimited contacts." });
                } else {
                  uiToast({ title: "Something went wrong. Please try again." });
                }
                return;
              }
              uiToast({ title: "Added!" });
              if (occasionDate) {
                setConvertedBabyDate(occasionDate);
              }
              fetchData();
            }}
            onDismiss={() => setBabyPromptData(null)}
          />
        );
      })()}

      {/* Contact Detail */}
      <ContactDetail
        open={detailOpen}
        onOpenChange={(v) => {
          setDetailOpen(v);
          if (!v) setInitialMessageContext(null);
        }}
        contact={selectedContact}
        occasions={selectedContact ? occasions.filter(o => o.contact_id === selectedContact.id) : []}
        onEdit={openEdit}
        onDeleted={fetchData}
        initialMessageContext={initialMessageContext}
      />

      {/* PaywallModal */}
      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        title={paywallProps.title}
        benefit={paywallProps.benefit}
      />

      {/* GiftBrowser from header icon */}
      <GiftBrowser
        open={giftBrowserOpen}
        onOpenChange={setGiftBrowserOpen}
      />
    </div>
  </>
  );
};

export default Dashboard;
